/* eslint-disable react/prop-types */
/* eslint-disable import/prefer-default-export */
import * as React from 'react'
import type { FormikProps } from 'formik'
import type { AccountInfoValues } from '../configurations'
import * as styles from '../../setup.steps.styl'
import { InputField } from '../../components'
import type { AccountInfoState } from '../index'
import type { StepProps } from '../../models'
import { passwordMessage } from '@src/utils/validation'
import { isEqual } from '@src/utils/comparison.utils'
import { isEmpty, find } from 'lodash'
import { Permissions, getPermissions } from '../helpers/permissions'
import cx from 'classnames'

export interface AccountInfoFormActions {
  toggleOldPassword: () => void
  toggleNewPassword: () => void
  changePassword: (oldPassword: string, newPassword: string) => void
}

export interface FormProps {
  formikProps: FormikProps<AccountInfoValues>
  stepProps: StepProps
  state: AccountInfoState
  actions: AccountInfoFormActions
}

export const AccountInfoForm: React.FC<FormProps> = React.memo(
  (props: FormProps) => {
    function hasPermission(permission: Permissions) {
      const { stepProps = {} as StepProps } = props
      const { user = {} } = stepProps
      const permissions = getPermissions(user)
      const foundPermission = !!find(
        permissions,
        permiss => permiss === (permission || '').toString()
      )
      return foundPermission
    }

    function renderValidationMessage(
      errorMsg: string | undefined
    ): React.ReactNode | undefined {
      return errorMsg ? (
        <div className={styles.errors} role="validation-error">
          <i className="material-icons">warning</i>
          <div>{errorMsg}</div>
        </div>
      ) : undefined
    }

    function renderNewPasswordValidation(
      value: string,
      errorMsg: undefined | string
    ): React.ReactNode {
      const passHint = value ? undefined : (
        <div className={styles.info}>{passwordMessage}</div>
      )
      const errorBlock = errorMsg
        ? renderValidationMessage(errorMsg)
        : undefined
      return (
        <>
          {passHint}
          {errorBlock}
        </>
      )
    }

    const {
      formikProps: {
        values,
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        initialValues
      },
      stepProps,
      state,
      actions
    } = props

    return (
      <form
        className={styles.accountInfoForm}
        onSubmit={handleSubmit}
        role="account-info-form"
      >
        <div className={styles.inputSection} role="input-section">
          <InputField
            name="firstName"
            label="First Name"
            handleChange={handleChange}
            handleBlur={handleBlur}
            value={values.firstName}
          />
          <InputField
            name="lastName"
            label="Last Name"
            handleChange={handleChange}
            handleBlur={handleBlur}
            value={values.lastName}
          />
          <InputField
            name="email"
            label="Email"
            value={values.email}
            readOnly
          />
          {hasPermission(Permissions.CanChangePasswords) && (
            <>
              <InputField
                className={cx(styles.password)}
                name="oldPassword"
                type={state.showOldPassword ? 'text' : 'password'}
                label="Old Password"
                handleChange={handleChange}
                handleBlur={handleBlur}
                value={values.oldPassword}
                togglePassword={actions.toggleOldPassword}
                validation={renderValidationMessage(errors.oldPassword)}
              />
              <InputField
                className={cx(styles.password)}
                type={state.showNewPassword ? 'text' : 'password'}
                name="newPassword"
                label="New Password"
                handleChange={handleChange}
                handleBlur={handleBlur}
                value={values.newPassword}
                togglePassword={actions.toggleNewPassword}
                validation={renderNewPasswordValidation(
                  values.newPassword,
                  errors.newPassword
                )}
              />
            </>
          )}
          <InputField
            name="jobTitle"
            label="Job Title"
            placeholder="e.g. Frontend Developer"
            handleChange={handleChange}
            handleBlur={handleBlur}
            value={values.jobTitle}
          />
        </div>
        {stepProps?.footerComponent
          ? React.createElement<StepProps>(stepProps.footerComponent, {
              ...stepProps,
              disabledNext:
                isEqual(initialValues, values, ['oldPassword']) ||
                !isEmpty(errors)
            })
          : null}
      </form>
    )
  }
)
